import { Accordion, AccordionDetails, Card, CardContent, CircularProgress, Grid, IconButton, Stack, Typography } from '@mui/material'
import { AccordionTitle } from 'components/styled/Accordions'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import StationAction from 'station/actions/StationAction'
import { STATION_NAME_ASSOCIATION, STATION_NAME_ASSOCIATION_REVERSE } from 'station/constants/StationConstants'
import i18n from 'simple-react-i18n'
import { keys, compact, groupBy, includes, toString } from 'lodash'
import { DOCUMENT, PICTURE } from 'components/file/constant/FileConstants'
import { getDate, getYear, getYearOrString } from 'utils/DateUtil'
import { getI18nTitleDataLength, normalizeString } from 'utils/StringUtil'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { getFileExtension, getFileInfo, getFileName, getFileNatureLabel } from 'utils/FileUtils'
import useSandreList from 'utils/customHook/useSandreList'
import Fancybox from 'components/fancybox/Fancybox'
import { Cancel, CheckBox, CheckBoxOutlineBlank, Edit } from '@mui/icons-material'
import DtoFile from 'components/file/dto/DtoFile'
import ConfirmModal from 'components/modal/ConfirmModal'
import { getSiteUrl } from 'utils/mapUtils/SiteTypes'
import UpdateFileDialog from './UpdateFileDialog'
import DtoQualitometer from 'quality/dto/DtoQualitometer'
import DtoPiezometer from 'piezometry/dto/DtoPiezometer'
import DtoHydrometricStation from 'hydrometry/dto/DtoHydrometricStation'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import DtoInstallation from 'installation/dto/installation/DtoInstallation'
import DtoDistributionUnit from 'distributionUnit/dto/DtoDistributionUnit'
import { ProductionUnitShape } from 'productionUnit/shapes/ProductionUnitShapes'
import ResourceDto from 'resource/dto/ResourceDto'
import { NoDataCard } from 'components/card/NoDataCard'

const DATE = 'date'
const FILE = 'file'
const ACTIVE = 'active'

const groupFiles = (files, group, typePictures, typeDocuments) => {
    switch (group) {
        case FILE: return groupBy(files, f => {
            const listTypes = f.fileType === PICTURE ? typePictures : typeDocuments
            return listTypes.find(t => t.mnemonique === f.type || t.name === f.type)?.name
        })
        case ACTIVE: return groupBy(files, file => file.active === '1' ? i18n.active : i18n.inactive)
        case DATE:
        default: return groupBy(files, file => getYearOrString(file.date, i18n.thisYear, ''))
    }
}

const FileCard = ({
    file = {},
    isEditMode = false,
    stationCode = '',

    onUpdate,
    onDelete,
}) => {
    const displayName = file.isAllDataName && stationCode === file.stationCode ? file.shortName : getFileName(file.name)

    const {
        sandreConstant,
        image,
    } = getFileInfo(file)

    const fileNatures = useSandreList(sandreConstant)

    return (
        <Fancybox>
            <a
                href={isEditMode ? undefined : file.url}
                target='_blank'
                data-fancybox={file.fileType == PICTURE && !isEditMode ? 'images' : undefined}
                data-caption={displayName}
            >
                <Card>
                    <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                            <Grid size={3} textAlign='center'>
                                {image}
                            </Grid>
                            <Grid container size={9} rowSpacing={0} columnSpacing={0.2}>
                                <Grid size='grow'>
                                    <Typography
                                        style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                        fontWeight='bold'
                                        variant='subtitle1'
                                        color='text.secondary'
                                        component='div'
                                    >
                                        {displayName}
                                    </Typography>
                                </Grid>
                                {isEditMode && (
                                    <>
                                        <Grid size='auto'>
                                            <IconButton
                                                color='primary'
                                                size='small'
                                                onClick={() => onUpdate(file.name)}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Grid>
                                        <Grid size='auto'>
                                            <IconButton
                                                color='primary'
                                                size='small'
                                                onClick={() => onDelete(file.name)}
                                            >
                                                <Cancel />
                                            </IconButton>
                                        </Grid>
                                    </>
                                )}
                                <Grid size={12}>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.type} : {getFileExtension(file.name)}
                                    </Typography>
                                </Grid>
                                <Grid size={12}>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.nature} : {getFileNatureLabel(file, fileNatures)}
                                    </Typography>
                                </Grid>
                                <Grid size={12}>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.date} : {getDate(file.date)}
                                    </Typography>
                                </Grid>
                                <Grid size={6} display='flex' alignItems='center'>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.active} :
                                    </Typography>
                                    {file.active === '1' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                                </Grid>
                                <Grid size={6} display='flex' alignItems='center'>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.shared} :
                                    </Typography>
                                    {file.shared ? <CheckBox /> : <CheckBoxOutlineBlank />}
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </a>
        </Fancybox>
    )
}

FileCard.propTypes = {
    file: PropTypes.shape({}),
    isEditMode: PropTypes.bool,
    stationCode: PropTypes.string,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
}

const StationsFileAccordion = ({
    station,
    group,
    filter,
    stationType,
    defaultExpanded = false,
    isEditMode = false,
    refetchFiles = false,
    setRefetchFiles = () => {},
    setAllFiles = () => {},
}) => {
    const [isLoaded, setLoaded] = useState(false)
    const [isOpen, setOpen] = useState(defaultExpanded)
    const [selectedFile, setSelectedFile] = useState()
    const [files, setFiles] = useState([])
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [isUpdateFileOpen, setIsUpdateFileOpen] = useState(false)

    const dispatch = useDispatch()

    const refetchDatas = () => {
        setLoaded(false)
        setFiles([])
        setAllFiles(prev => prev.filter(prevF => !includes(prev.map(f => f.url), prevF.url)))
    }

    useEffect(() => {
        if (refetchFiles) {
            refetchDatas()
            setRefetchFiles(false)
        }
    }, [refetchFiles])

    useEffect(() => {
        if (isOpen && !isLoaded) {
            const code = station?.stationLinkedCode ?? station?.code
            // le nom du type (ex. 'hydrometry') : file-service ne retrouve pas les fichiers avec le numéro de type (ex. 4)
            // pour une station associée, typeName est le type de la station liée
            const type = station?.typeName ?? station?.stationLinkedType
            Promise.all([
                dispatch(StationAction.fetchFiles(code, toString(type)?.toUpperCase()))
                    .then((documents = []) => {
                        const formattedDocuments = documents.map(document => new DtoFile(document, DOCUMENT))
                        setAllFiles(prev => [...prev, ...formattedDocuments])
                        setFiles(prev => [...prev, ...formattedDocuments])
                    }),
                dispatch(StationAction.fetchPictures(code, toString(type)?.toUpperCase()))
                    .then((pictures = []) => {
                        const formattedPictures = pictures.map(picture => new DtoFile(picture, PICTURE))
                        setAllFiles(prev => [...prev, ...formattedPictures])
                        setFiles(prev => [...prev, ...formattedPictures])
                    }),
            ]).finally(() => setLoaded(true))
        }
    }, [isOpen, isLoaded])

    const typePictures = useSandreList(SANDRE.TYPE_PHOTO)
    const typeDocuments = useSandreList(SANDRE.TYPE_DOCUMENT)

    const filteredFiles = useMemo(() => {
        const {
            searchValue,
            extension,
            naturePicture,
            natureDocument,
            year,
            active,
        } = filter

        const search = normalizeString(searchValue)
        const typePicture = typePictures.find(t => t.mnemonique === naturePicture || t.name === naturePicture)
        const typeDocument = typeDocuments.find(t => t.mnemonique === natureDocument || t.name === natureDocument)

        const filterByName = search ? files.filter(f => normalizeString(f.shortName).includes(search)) : files
        const filterByActive = active ? filterByName.filter(f => f.active === active) : filterByName
        const filterByYear = year ? filterByActive.filter(f => getYear(f.date) === year) : filterByActive
        const filterByExtension = extension ? filterByYear.filter(f => getFileExtension(f.name) === extension) : filterByYear
        const filterByNaturePicture = naturePicture ? filterByExtension.filter(f => {
            return f.fileType !== PICTURE || typePicture.mnemonique === f.type || typePicture.name === f.type
        }) : filterByExtension
        const filterByNatureDocument = natureDocument ? filterByNaturePicture.filter(f => {
            return f.fileType === PICTURE || typeDocument.mnemonique === f.type || typeDocument.name === f.type
        }) : filterByNaturePicture
        return filterByNatureDocument
    }, [filter, files, typePictures, typeDocuments])

    const groups = useMemo(() => groupFiles(filteredFiles, group, typePictures, typeDocuments), [filteredFiles, group])

    const getAccordionTitle = (code, name, type) => {
        const title = code && name ? `${code} - ${name}` : code
        return (
            <Grid container alignItems={'center'} spacing={1}>
                <img src={getSiteUrl(STATION_NAME_ASSOCIATION[type] ?? STATION_NAME_ASSOCIATION_REVERSE[type])}
                    style={{ maxHeight: '32px' }}
                />
                {title}
            </Grid>
        )
    }

    // un sous-accordéon par groupe (date, type…) ; une carte « aucun fichier » si la station n'a ni photo ni document
    const getFileGroups = () => {
        const fileGroups = compact(keys(groups).reverse().map((key, i) => {
            const fileCards = compact(groups[key].map(f => {
                // code de la station de l'accordéon : pour une station associée, c'est celui de la station liée
                // (station.code est celui de la station sur laquelle on est)
                if ((station?.stationLinkedCode ?? station?.code) !== f.stationCode && !f.shared) return undefined
                return (
                    <Grid key={f.name} size={3}>
                        <FileCard
                            file={f}
                            isEditMode={isEditMode}
                            stationCode={station?.stationLinkedCode ?? station.code}
                            onUpdate={name => {
                                setSelectedFile(name)
                                setIsUpdateFileOpen(true)
                            }}
                            onDelete={name => {
                                setSelectedFile(name)
                                setIsConfirmOpen(true)
                            }}
                        />
                    </Grid>
                )
            }))
            if (!fileCards.length) return undefined
            return (
                <Grid key={key} size={12}>
                    <Accordion defaultExpanded={i === 0}>
                        <AccordionTitle
                            title={`${key} (${fileCards.length} ${getI18nTitleDataLength(i18n.element, i18n.elements, fileCards.length)})`}
                        />
                        <AccordionDetails>
                            <Grid container spacing={1}>
                                {fileCards}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            )
        }))
        return fileGroups.length ? fileGroups : <NoDataCard message={i18n.noFileOrPicture} />
    }

    return (
        <Grid size={12}>
            <Accordion expanded={isOpen} onChange={(_, expanded) => setOpen(expanded)}>
                <AccordionTitle
                    title={station.stationLinkedCode ?
                        getAccordionTitle(station.stationLinkedCode, station.stationLinkedName, station.stationLinkedType)
                        :
                        getAccordionTitle(station.code, station.name, station.typeName)
                    }
                />
                <AccordionDetails>
                    <Stack spacing={1}>
                        {isLoaded ?
                            getFileGroups()
                            :
                            (
                                <Grid container padding={1} justifyContent={'center'} size={12}>
                                    <CircularProgress />
                                </Grid>
                            )
                        }
                    </Stack>
                </AccordionDetails>
            </Accordion>
            <UpdateFileDialog
                isOpen={isUpdateFileOpen}
                onClose={() => setIsUpdateFileOpen(false)}
                fileName={selectedFile}
                files={files}
                station={station}
                stationType={stationType}
                setLoaded={refetchDatas}
                linkSamplePoints={station.link_samplePoints || station.link_pointPrels}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title={i18n.deletingConfirmation}
                subTitle={selectedFile}
                onClose={() => setIsConfirmOpen(false)}
                onValidate={() => {
                    const file = files.find(f => f.name === selectedFile)
                    dispatch(StationAction.deleteFile(file)).then(() => {
                        setIsConfirmOpen(false)
                        refetchDatas()
                    })
                }}
            />
        </Grid>
    )
}

StationsFileAccordion.propTypes = {
    station: PropTypes.oneOfType([
        PropTypes.instanceOf(DtoQualitometer),
        PropTypes.instanceOf(DtoPiezometer),
        PropTypes.instanceOf(DtoHydrometricStation),
        PropTypes.instanceOf(PluviometerDto),
        PropTypes.instanceOf(DtoInstallation),
        PropTypes.instanceOf(DtoDistributionUnit),
        ProductionUnitShape,
        PropTypes.instanceOf(ResourceDto),
    ]),
    group: PropTypes.string,
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        extension: PropTypes.string,
        naturePicture: PropTypes.string,
        natureDocument: PropTypes.string,
        year: PropTypes.string,
        active: PropTypes.string,
    }),
    stationType: PropTypes.string,
    defaultExpanded: PropTypes.bool,
    isEditMode: PropTypes.bool,
    refetchFiles: PropTypes.bool,
    setRefetchFiles: PropTypes.func,
    setAllFiles: PropTypes.func,
}

export default StationsFileAccordion